import { CurrencyUnit, NumberScales } from '@/shared/consts';
import { getLocale, t } from '@/i18n';
import { signedFixed } from './price-tone';

/**
 * 앱 i18n 로케일 → Intl BCP47 태그 (TRAD-2311).
 *
 * 🔴 **로케일 태그의 단일 진입점.** 이전에는 화면마다 `toLocaleString('ko-KR')` 을 직접 박아 두어
 * **영어 사용자에게도 항상 한국식 표기**가 나갔고, 같은 규칙이 `polymarket/utils`·`ProposalFormPage`
 * 에 각각 복제돼 있었다. 여기 하나만 두고 전부 이걸 쓴다.
 *
 * `navigator.language` 를 직접 보지 않고 `getLocale()` 을 거치는 것이 중요하다 — 사용자가 언어
 * 토글로 **명시 선택한 로케일**(localStorage)이 브라우저 언어를 이겨야 하기 때문이다.
 *
 * TRAD-2723 — 🔴 **`en-US` 폴백은 "빠뜨린 것"이 아니라 의도다. 되돌리지 말 것.**
 * 입력이 브라우저 로케일이 아니라 **앱 로케일**이고, 앱은 지금 `ko`/`en` 카탈로그만 갖고
 * 있다. 3번째 로케일이 오는 날은 그 언어 카탈로그가 새로 생기는 날이고, **그때는 이 함수를
 * 반드시 손대야 한다** — 지금 `en-US` 로 떨어지는 건 "틀리게 나오는 것"이 아니라 "그 로케일이
 * 아직 지원되지 않아 영어 표기로 나오는 것"이다(화면의 나머지 문구도 영어 폴백이라 일관된다).
 * 팀장 결정(2026-08-19), 이 함수의 소비자 3갈래 전부에 동일 적용:
 *   ①숫자 포맷 — `formatLocalizedNumber`(이 파일)
 *   ②날짜·상대시간 포맷 — `formatLocalizedDate`(이 파일) · `timeAgo`(dateDisplay.tsx)
 *     · `journal/lib/labels.ts` · `polymarket/utils.ts`(`formatMarketDateTime`)
 *     · `ProposalFormPage.tsx:777`
 *   ③통화·단위체계 분기(Intl 호출이 아니라 "어느 단위 언어를 쓸지"의 비즈니스 분기 —
 *     `getIntlLocale() === 'ko-KR'` 로 억/만/평 vs M/B/㎡ 를 가른다) — `formatKrwScaled`
 *     (이 파일) · `real-estate-format.ts` 의 `formatDealPrice`(~~·`formatArea`~~ — TRAD-3508 로 로케일 무관 ㎡). 억·만·평은
 *     한국 고유 단위라 "모르는 로케일 → 국제 단위(M/B/㎡)"가 ①의 "en-US 가 중립"과 같은
 *     논리로 안전한 기본값이다.
 * 이 가드가 못 잡는 것: "3번째 로케일이 추가됐다"는 잡지만 **"그 로케일에 맞는 날짜·단위
 * 표기가 무엇인가"는 알려주지 않는다** — 그건 그 로케일을 실제로 추가하는 사람이 판단할 몫이다.
 * `__tests__/format.test.ts` 의 통합 가드가 이 방향을 고정한다.
 */
export const getIntlLocale = (): string => (getLocale().startsWith('ko') ? 'ko-KR' : 'en-US');

/**
 * 보는 사람의 로케일로 숫자를 포맷한다.
 *
 * ⚠️ **자릿수·반올림은 호출부가 그대로 넘긴다.** 이 함수는 표기 경로만 바꿀 뿐 금액 정책
 * (반올림·자릿수·단위 선택)을 건드리지 않는다 — 정책은 TRAD-2206 결정 대기.
 */
export const formatLocalizedNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
  value.toLocaleString(getIntlLocale(), options);

/**
 * 보는 사람의 로케일로 **날짜만** 포맷한다 (TRAD-2311 배치③).
 *
 * ⚠️ 타임존 옵션은 호출부가 정한다. **date-only 값**(예: `yyyy-MM-dd`)을 다룰 때는 반드시
 * `timeZone: 'UTC'` 를 넘겨야 한다 — 안 그러면 UTC 뒤쪽 타임존 사용자에게 **날짜가 하루 밀린다**.
 * 🔸 TRAD-3672: 서버가 준 **순간**을 날짜로 보여 줄 때는 이 함수가 아니라 아래 `formatKstDate`(KST 달력)를 쓴다.
 *    timeZone 없이 이 함수를 부르는 자리는 «현지 0시 Date» 4곳으로 고정돼 있다(허용목록 가드).
 */
export const formatLocalizedDate = (date: Date, options?: Intl.DateTimeFormatOptions): string =>
  date.toLocaleDateString(getIntlLocale(), options);

/**
 * TRAD-3672 — **순간(instant)이 뜻하는 «날짜»를 KST 달력으로** 포맷한다. 로케일은 보는 사람, 달력은 KST.
 *
 * 서버가 순간으로 내려주는 값(`Z`·`+09:00`·오프셋 없는 KST 벽시계)을 «날짜만» 보여 주는 자리용이다 — 평가일 ·
 * 포인트 적립일 · 관심 단지 등록일 · 뉴스 날짜(davidyoon 결정 Q2 «날짜·시각은 KST», 2026-10-09). 종전
 * `formatLocalizedDate` 는 보는 사람 시간대로 찍어 LA 에선 KST 자정~오후 4시 사이 값이 **하루 앞**,
 * Kiritimati(UTC+14)에선 KST 오전 5시~자정 사이 값이 **하루 뒤**로 보였다.
 *
 * 🔴 `timeZone` 은 호출부 옵션보다 **뒤에** 넣는다 — 호출부가 무엇을 넘겨도 KST 로 고정(파라미터로 열지 않는다,
 * `kst-calendar.ts` 머리말과 같은 원칙). Asia/Seoul 은 DST 가 없어 IANA 이름이 고정 오프셋과 같다.
 *
 * ⚠️ **«현지 0시로 만든 Date»에는 쓰지 않는다.** `new Date(y, m-1, d)` / `T00:00:00`(오프셋 없음)으로 만든 값은
 * 보는 사람 시간대의 자정이라 KST 로 찍으면 KST 동쪽(Kiritimati 등)에서 하루 앞당겨진다. 그런 자리(금융 캘린더 제목 ·
 * 부동산 «N월 기준» · 경제지표 툴팁)는 `formatLocalizedDate` 그대로가 맞다 — 허용목록 가드
 * (`__tests__/format-localized-date-callers-3672.test.ts`)가 그 4곳만 timeZone 없이 부르도록 고정한다.
 * (`kst-calendar.ts` 가 아니라 여기 둔 이유: 그 파일은 «Intl·로컬 존 API 를 쓰지 않는다»를 구조적 보증으로 삼는다.)
 */
export const formatKstDate = (instant: Date, options?: Intl.DateTimeFormatOptions): string =>
  formatLocalizedDate(instant, { ...options, timeZone: 'Asia/Seoul' });

/**
 * 원화 금액 표기. **단위 문자열을 코드에 박지 않고 카탈로그를 거친다**
 * (ko `"{amount}원"` / en `"{amount} KRW"`).
 *
 * 숫자 자체는 `formatLocalizedNumber` 와 같은 규칙이라 **표시되는 자릿수는 종전과 동일**하다.
 */
export const formatKrwAmount = (value: number, options?: Intl.NumberFormatOptions): string =>
  t('format.krw_amount', { amount: formatLocalizedNumber(value, options) });

/** `1.5` 는 그대로, `150.0` 은 `150` 으로 — 스케일 표기에 의미 없는 `.0` 을 남기지 않는다. */
const trimScale = (value: number): string => String(Number(value.toFixed(1)));

/**
 * TRAD-2581(§8-2 P5, amount-format-policy.md) — `억` 단위 스케일 값을 정책 규칙대로 표시한다.
 *
 * 이전에는 정수부 자릿수와 무관하게 `.toFixed(1)`만 해서 1조원이 `10000.0억원`으로 나왔다.
 * P5: ①유효숫자 3자리 ②소수 최대 1자리 ③정수부 3자리 이상이면 소수 없음
 * ④정수부 4자리 이상이면 천단위 구분자 필수.
 *
 * - ~~정수부가 1~2자리(< 100)면 소수 1자리를 **항상** 유지한다(`5억원`이 아니라 `5.0억원` —
 *   기존에 이미 테스트로 고정돼 있던 동작이라 그대로 보존한다. `trimScale`처럼 `.0`을
 *   지우면 이 자리에서 회귀가 난다).~~
 *   → 🔴 TRAD-2581 A-7(2026-10-09) 정정: 정본은 결정 B(Confluence 274366465 v2, 2026-08-04 확정 «0이면 생략»)다 —
 *   `1억원`·`20억원`·`4억원`(3.95억 반올림). 위 «보존»은 결정 B 이전 테스트를 지키려던 것이었다.
 *   iOS·android 와 같은 벡터(docs-ops/2026-10-09-realestate-trade-number-format-3platform.md)로 맞춘다.
 * - 정수부가 3자리 이상(>= 100)이면 정수로 반올림 + 로케일 구분자 — `375`/`3,753`/`10,000`.
 *   🔴 A-7: 100 판정은 **반올림 후** 값으로 한다 — 99.95억이 `100.0억원`이 되던 결함(반올림 전 판정).
 * - 🔴 A-7: 판정·반올림은 원 정수에서 «0.1억(천만원) 단위 정수»로 한다. `3.95.toFixed(1)` 같은 이진 부동소수
 *   반올림에 기대지 않는다(3.95 는 3.9500…018 이라 우연히 맞지만 1.775 같은 값은 내림이 된다 — TRAD-3663).
 *
 * `만` 분기(`formatLocalizedNumber(Math.round(...))`)와 대칭이 되도록, >=100 구간은 로케일
 * 구분자 소스를 `formatLocalizedNumber`로 통일한다(그 함수만 구분자를 쓰고 이 함수는 안
 * 쓰던 것이 이 버그의 근본 원인이었다 — 같은 파일 안에서 분기마다 다른 방식).
 */
const formatEokValue = (won: number): string => {
  const tenths = Math.round(won / 10_000_000); // 0.1억 단위
  if (tenths >= 1000) return formatLocalizedNumber(Math.round(won / 100_000_000));
  return tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toFixed(1);
};

/**
 * 큰 금액의 **축약 표기**. 로케일마다 쓰는 수 단위 체계가 다르다 (TRAD-2311, 팀장 결정 B안).
 *
 * - **KO — 현행 유지**: `1.5억원` / `3,000만원` / `1,234원`
 * - **EN — 백만(M) 기준**: `150M KRW` / `30M KRW` / `1,234 KRW`
 *
 * 🔴 **1 미만 소수가 나오는 단위는 쓰지 않는다.** `0.4B KRW` 같은 표기는 읽는 사람이 자릿수를
 * 잘못 세게 만든다(TRAD-2206 이 지적한 함정). 그래서 EN 은 **10억 이상에서만 B**, 100만 이상은 M,
 * 그 미만은 축약 없이 그대로 쓴다. 각 분기의 최소값이 항상 `1` 이상이 되도록 경계를 잡았다.
 *
 * `억`·`만` 은 한국 고유 수 단위라 영어에 대응어가 없다 — 그래서 이것만은 **표기 경로 이관이
 * 아니라 단위 선택 자체를 로케일별로 가른다.** 가리키는 금액은 양쪽이 같다(1.5억원 = 150M KRW).
 *
 * ⚠️ EN 스케일 선택은 **TRAD-2206 최종 결정 시 조정될 수 있다**(Confluence 274366465).
 *
 * TRAD-2723 — `!== 'ko-KR'` 분기(3번째 로케일 포함)가 국제 단위(M/B)로 떨어지는 것은
 * 의도다. 억·만은 한국 고유 단위라 "모르는 로케일 → 국제 단위"가 안전한 기본값이다.
 * `getIntlLocale()` 상단 주석(③) 참고 — 되돌리지 말 것.
 */
export const formatKrwScaled = (amount: number): string => {
  if (getIntlLocale() === 'ko-KR') {
    if (amount >= 100_000_000) return t('format.krw_scale_eok', { value: formatEokValue(amount) });
    if (amount >= 10_000)
      return t('format.krw_scale_man', { value: formatLocalizedNumber(Math.round(amount / 10_000)) });
    return formatKrwAmount(amount);
  }

  if (amount >= 1_000_000_000) return t('format.krw_scale_billion', { value: trimScale(amount / 1_000_000_000) });
  if (amount >= 1_000_000) return t('format.krw_scale_million', { value: trimScale(amount / 1_000_000) });
  return formatKrwAmount(amount);
};

/**
 * 값이 **없다는 사실**을 말하는 자리표시자. 숫자가 아니므로 로케일 무관이다 (TRAD-2370).
 *
 * `0` 과 반드시 구분된다 — `0` 은 "거래가 없었다"는 **사실 주장**이고, 이것은
 * "우리가 값을 받지 못했다"는 **부재의 표시**다. 둘을 같은 글자로 그리면 화면이 거짓말을 한다.
 */
export const VALUE_UNAVAILABLE = '—';

/**
 * 24시간 거래대금 표기 — **미제공(null)과 실제 0을 가른다** (TRAD-2370).
 *
 * ## 왜 필요한가
 * 같은 값을 세 곳이 **제각각** 그리고 있었다:
 * - `entities/market/lib/normalize.ts` — 서버가 안 주면 `tradePrice × volume` 으로 **추정치를
 *   만들어** 서버 값인 것처럼 내놨다. 볼륨까지 없으면 그 곱이 `0` 이 된다 → **"0" 이라는 거짓말.**
 * - 그리드 `valueFormatter` — `if (!value) return ''` 로 **0 과 null 을 똑같이 빈칸**으로 접었다.
 * - `PriceBar` — undefined 면 빈칸, `0` 이면 `"0"`. 게다가 로케일이 `'en-US'` 하드코딩.
 *
 * 이제 **두 화면이 이 한 함수를 부른다.** 조건을 각자 복사하면 "목록에서 본 값 ≠ 상세에서 본 값"이
 * 된다(TRAD-2310 의 교훈).
 *
 * 🔴 **`!value` 로 판정하지 마라.** `0` 이 falsy 라서 실제 0 거래가 "미제공"으로 둔갑한다.
 * 반드시 `== null` 로 **부재만** 가른다.
 */
/** BTC·USDT 마켓 판별 — `formatPrice` 와 **같은 규칙**을 쓴다(둘이 갈리면 같은 행에서 가격과 거래대금의 기준이 달라진다). */
export const isCryptoQuoteMarket = (market: string | null | undefined): boolean =>
  typeof market === 'string' && (market.startsWith('BTC-') || market.startsWith('USDT-'));

/** 마켓 코드에서 기준 통화(quote) 심볼을 뽑는다. `BTC-ETH` → `BTC`. */
const quoteSymbol = (market: string): string => market.split('-')[0] ?? '';

/**
 * 24시간 누적 **거래대금** 표기.
 *
 * 🔴 TRAD-2482 — 종전엔 마켓 구분 없이 `maximumFractionDigits: 0` 이었다. KRW 마켓(수십억 원)엔 맞지만
 * **BTC·USDT 마켓의 거래대금은 소수**(예 `0.16 BTC`)라 정수로 자르면 **`0` 으로 뭉개진다**
 * — "거래가 없었다"와 구분이 안 되는 거짓 표시다.
 *
 * 그래서 마켓을 받아 기준 통화별로 가른다:
 * - `BTC-`/`USDT-` 마켓 → 소수 유지(최대 8자리, Intl 이 뒤쪽 0 은 자동으로 떨어뜨린다) + 단위 병기
 * - 그 외(KRW) → 종전 그대로 정수
 *
 * `market` 을 안 주면 종전 동작(정수)이다 — 마켓을 모르는 호출부가 있어도 회귀하지 않는다.
 * 단위 문자열은 코드에 박지 않고 카탈로그(`format.amount_with_unit`)를 거친다.
 */
export const formatTradeAmount = (value: number | null | undefined, market?: string | null): string => {
  // 🔴 TRAD-2581(3사 결정 2026-10-08) — TRAD-2382 규칙 개정: 24h 거래대금은 0 · null · 음수 → 부재 표기.
  // 종전 «0 은 정상 값(거래 없었다)»은 «거래대금 0» 이 실제로는 집계 누락·미상장과 구분되지 않아 3사가 부재로 맞췄다.
  // 글리프는 web 기존 VALUE_UNAVAILABLE(«—») — 같은 화면에서 null 과 0 이 다른 글자로 갈리지 않게.
  // 🔴 «정확히 0» 만이다 — BTC·USDT 마켓의 작은 양수(0.00000123)는 아래 8자리 표기 그대로. 거래량(formatTradeVolume)은 무변경.
  if (value == null || value <= 0) return VALUE_UNAVAILABLE;

  if (isCryptoQuoteMarket(market)) {
    return t('format.amount_with_unit', {
      amount: formatLocalizedNumber(value, { maximumFractionDigits: 8 }),
      unit: quoteSymbol(market as string),
    });
  }

  return formatLocalizedNumber(value, { maximumFractionDigits: 0 });
};

/**
 * 24시간 누적 **거래량**(코인 수량) 표기 — TRAD-2382.
 *
 * `formatTradeAmount`(거래대금)와 **같은 규칙**을 쓰되 소수 자릿수만 다르다(수량은 소수가 의미를
 * 갖는다). 거래대금이 TRAD-2370 에서 고쳐질 때 **거래량은 같은 결함을 그대로 안고 남아 있었다**:
 * - 정규화가 `raw.accTradeVolume24h ?? 0` 으로 **부재를 0으로 위장**했고,
 * - 그리드 `valueFormatter` 는 `if (!value) return ''` 로 **0 과 null 을 똑같이 빈칸**으로 접었고,
 * - `MarketInfoCard` 는 `volume24h?.toLocaleString('en-US')` 로 부재를 빈칸 + 로케일 하드코딩.
 *
 * 🔴 `!value` 로 판정하지 마라 — `0`("거래가 없었다")이 falsy 라 "미제공"으로 둔갑한다.
 * `== null` 로 **부재만** 가른다. 두 화면(목록·상세)이 이 한 함수를 부르므로 표기가 갈리지 않는다.
 */
export const formatTradeVolume = (value: number | null | undefined): string =>
  value == null ? VALUE_UNAVAILABLE : formatLocalizedNumber(value, { maximumFractionDigits: 2 });

export const currencyFormatter = (
  currency: number,
  decimalPlaces: number = 2,
  showNumericalScales: boolean = true,
  unit: keyof typeof CurrencyUnit = 'en',
) => {
  if (!currency) return '';
  let result = '';
  const abs = Math.abs(currency);
  if (showNumericalScales) {
    // unit 표현
    if (abs >= 1_000_000_000_000) {
      result += `${Number((currency / 1_000_000_000_000).toFixed(decimalPlaces)).toLocaleString()}${NumberScales.trillion}`;
    } else if (abs >= 1_000_000_000) {
      result += `${Number((currency / 1_000_000_000).toFixed(decimalPlaces)).toLocaleString()}${NumberScales.billion}`;
    } else if (abs >= 1_000_000) {
      result += `${Number((currency / 1_000_000).toFixed(decimalPlaces)).toLocaleString()}${NumberScales.million}`;
    } else if (abs >= 1_000) {
      result += `${Number((currency / 1_000).toFixed(decimalPlaces)).toLocaleString()}${NumberScales.thousand}`;
    } else {
      const round = Number(currency.toFixed(decimalPlaces));
      result += round === 0 ? 0 : round.toLocaleString();
    }
  }
  return `${CurrencyUnit?.[unit]}${result}`;
};

export const percentFormatter = (decimal: number) => {
  const pct = (decimal || 0) * 100;
  // TRAD-3670 — 반올림 0(0.00004 → «0.00%»)은 부호 없이 · «-0.00%» 금지. 0 이 아닌 값은 종전과 한 글자도 같다.
  // ~~const prefix = pct > 0 ? '+' : ''; return `${prefix}${pct.toFixed(2)}%`;~~
  return `${signedFixed(pct, 2)}%`;
};

/**
 * 가격 구간별 소수점 자릿수를 반환합니다. (업비트 기준)
 *
 * >= 100       : 0자리  (98,765,000)
 * >= 10        : 1자리  (54.3)
 * >= 1         : 2자리  (1.23)
 * >= 0.01      : 4자리  (0.1234)
 * >= 0.0001    : 6자리  (0.001234)
 * < 0.0001     : 8자리  (0.00001234)
 */
export const getPriceFractionDigits = (price: number): number => {
  const abs = Math.abs(price);
  if (abs >= 100) return 0;
  if (abs >= 10) return 1;
  if (abs >= 1) return 2;
  if (abs >= 0.01) return 4;
  if (abs >= 0.0001) return 6;
  return 8;
};

/**
 * 마켓 타입에 따라 가격을 포맷합니다.
 * KRW 마켓: 업비트 기준 구간별 소수점 적용
 * BTC/USDT 마켓: 소수점 8자리
 */
export const formatPrice = (price: number, market: string): string => {
  if (price == null) return '-';
  // 🔴 TRAD-2482 — 종전엔 `toLocaleString('en-US')` 로 **로케일이 하드코딩**돼 있었다. 같은 파일의
  // `formatTradeAmount`/`formatTradeVolume` 은 `formatLocalizedNumber`(로케일 반영)를 쓰는데 이 함수만
  // 달라서, 한 화면 안에서도 숫자 구분 표기의 기준이 갈릴 수 있었다. 자릿수 규칙은 그대로 두고
  // 로케일 소스만 통일한다(값·자릿수 불변, 구분자만 로케일을 따른다).
  if (isCryptoQuoteMarket(market)) {
    return formatLocalizedNumber(price, { maximumFractionDigits: 8, minimumFractionDigits: 0 });
  }
  const digits = getPriceFractionDigits(price);
  return formatLocalizedNumber(price, { maximumFractionDigits: digits, minimumFractionDigits: digits });
};

/**
 * TRAD-3676 — **등락폭(전일 대비 금액)** 표기. 3사 규칙(Jira TRAD-3676 · 업비트 실화면 4표본 2026-10-10):
 *  - 자릿수 **상한** = 현재가(`referencePrice`)의 가격 구간 자릿수(`getPriceFractionDigits`)
 *  - **하한 0** — 끝의 0 은 지운다. 0 은 어느 구간이든 «0»
 *  - 크립토 쿼트 마켓(BTC/USDT 마켓)은 종전 `formatPrice` 그대로(상한 8 · 하한 0)
 * 업비트 표본: WAVES 369 → «0» · DOOD 2.14 → «0» · LSK 323 → «1» · SHIB 0.00749 → «0.00001».
 *
 * 🔴 종전엔 `formatPrice(change, market)` — 자릿수를 **등락폭 자기 크기**로 골라 0 이 «1 미만 가격»으로 읽혀
 * «0.00000000»(8자리), 정수 구간의 −4 가 «-4.00» 이 됐다.
 * 🔸 상한이 «가격 구간»인지 «호가 단위»인지는 업비트 4표본으로 못 갈랐다(미결 — Jira 기록). 구간으로 간다.
 * 부호(«+»)는 호출부가 붙인다(음수는 이 함수가 «-» 를 낸다). 반올림 결과가 0 이면 «-0» 이 아니라 «0».
 */
export const formatChangePrice = (change: number, referencePrice: number, market: string): string => {
  if (isCryptoQuoteMarket(market)) return formatPrice(change, market);
  const digits = getPriceFractionDigits(referencePrice);
  const rounded = Number(change.toFixed(digits));
  return formatLocalizedNumber(rounded === 0 ? 0 : rounded, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
};

/**
 * 숫자 입력 필드 표시용: 정수부에 천단위 콤마, 소수부 원형 보존.
 * 예) 1234567.89 → "1,234,567.89"
 * 빈 문자열/NaN → ""
 */
export const formatNumberInput = (value: number | string): string => {
  const str = String(value);
  if (str === '' || str === 'undefined' || str === 'NaN') return '';
  const [intPart, decPart] = str.split('.');
  const formattedInt = Number(intPart).toLocaleString('en-US');
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

/**
 * 콤마 포함 문자열을 number로 파싱. 빈 값·비숫자 → undefined.
 * 예) "1,234,567.89" → 1234567.89 / "100,000" → 100000 / "" → undefined
 */
export const parseNumberInput = (value: string): number | undefined => {
  const stripped = value.replace(/,/g, '');
  if (stripped === '') return undefined;
  const n = parseFloat(stripped);
  return isNaN(n) ? undefined : n;
};
